/**
 * Mapping between a `HighScore` and one `public.race_runs` row in Helmway. Pure: the fetch lives
 * in `src/ui/play/raceRunsRemote.ts`.
 *
 * `document` is the whole score (stats, hull, replay) and `schema_version` is the high-score
 * version it was written at, so a later score bump still migrates in TypeScript rather than in a
 * new SQL column. The indexed columns are Postgres integers (a fractional time is rejected) and
 * only order the table; `document` keeps the exact values.
 */

import { CURRENT_HIGH_SCORE_VERSION, migrateHighScores } from './migrate.ts';
import { HIGH_SCORE_LIMIT, compareScores, type RankedTimes } from './rank.ts';
import type { HighScore, HighScoreDocument } from './types.ts';

/** One `race_runs` insert. `id`, `player_id`, and `inserted_at` are left to the table defaults. */
export type RaceRunRow = {
  map_id: string;
  recorded_at: number;
  calculated_time_ms: number;
  time_ms: number;
  rounds: number;
  boat_id: string;
  schema_version: number;
  document: HighScore;
  rig: string;
  channel: string;
};

/** A remote run after migrate, keyed to its catalog map. */
export type RaceRun = {
  mapId: string;
  score: HighScore;
};

/** The columns selected to rank the table before any `document` is downloaded. */
export const RACE_RUN_INDEX_COLUMNS = 'id,map_id,calculated_time_ms,time_ms,recorded_at';
/** The columns `fromRaceRunRow` reads. */
export const RACE_RUN_DOCUMENT_COLUMNS = 'map_id,schema_version,document';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

export function toRaceRunRow(
  score: HighScore,
  mapId: string,
  rig: string,
  channel: string,
): RaceRunRow {
  return {
    map_id: mapId,
    recorded_at: Math.round(score.recordedAt),
    calculated_time_ms: Math.round(score.calculatedTimeMs),
    time_ms: Math.round(score.timeMs),
    rounds: Math.round(score.rounds),
    boat_id: score.boat.id,
    schema_version: CURRENT_HIGH_SCORE_VERSION,
    document: score,
    rig,
    channel,
  };
}

/** Migrates one fetched row, or null when it is not a run this build can read. */
export function fromRaceRunRow(row: unknown): RaceRun | null {
  if (
    !isRecord(row) ||
    typeof row.map_id !== 'string' ||
    !row.map_id ||
    typeof row.schema_version !== 'number'
  ) {
    return null;
  }

  const migrated = migrateHighScores({
    version: row.schema_version,
    scores: { [row.map_id]: [row.document] },
  });
  const score = migrated?.scores[row.map_id]?.[0];

  return score ? { mapId: row.map_id, score } : null;
}

type IndexedRun = RankedTimes & { id: string; mapId: string };

function indexedRun(row: unknown): IndexedRun | null {
  if (!isRecord(row) || typeof row.id !== 'string' || typeof row.map_id !== 'string') {
    return null;
  }

  const calculatedTimeMs = asFiniteNumber(row.calculated_time_ms);
  const timeMs = asFiniteNumber(row.time_ms);
  const recordedAt = asFiniteNumber(row.recorded_at);

  if (calculatedTimeMs === null || timeMs === null || recordedAt === null) {
    return null;
  }

  return { id: row.id, mapId: row.map_id, calculatedTimeMs, timeMs, recordedAt };
}

/**
 * Ids of the best `limit` rows per map from `RACE_RUN_INDEX_COLUMNS` rows, in rank order. Every
 * finish stays in Helmway; only these documents need to reach the browser.
 */
export function topRaceRunIds(
  rows: readonly unknown[],
  limit: number = HIGH_SCORE_LIMIT,
): string[] {
  const ranked = rows
    .flatMap((row) => {
      const run = indexedRun(row);
      return run ? [run] : [];
    })
    .sort(compareScores);
  const keptPerMap = new Map<string, number>();
  const ids: string[] = [];

  for (const run of ranked) {
    const kept = keptPerMap.get(run.mapId) ?? 0;

    if (kept < limit) {
      keptPerMap.set(run.mapId, kept + 1);
      ids.push(run.id);
    }
  }

  return ids;
}

function runKey(score: HighScore): string {
  return `${score.recordedAt}:${score.boat.id}`;
}

/**
 * Local cache after a fetch: remote and local runs per map, one copy of each finish (same map,
 * `recordedAt`, and hull id — the table's unique key), best `HIGH_SCORE_LIMIT` kept.
 */
export function mergeRaceRuns(
  remote: readonly RaceRun[],
  local: HighScoreDocument,
): HighScoreDocument {
  const byMap = new Map<string, Map<string, HighScore>>();
  const add = (mapId: string, score: HighScore): void => {
    let runs = byMap.get(mapId);

    if (!runs) {
      runs = new Map();
      byMap.set(mapId, runs);
    }

    const key = runKey(score);

    if (!runs.has(key)) {
      runs.set(key, score);
    }
  };

  for (const run of remote) {
    add(run.mapId, run.score);
  }

  for (const [mapId, scores] of Object.entries(local.scores)) {
    for (const score of scores) {
      add(mapId, score);
    }
  }

  const scores: Record<string, HighScore[]> = {};

  for (const [mapId, runs] of byMap) {
    scores[mapId] = [...runs.values()].sort(compareScores).slice(0, HIGH_SCORE_LIMIT);
  }

  return { version: CURRENT_HIGH_SCORE_VERSION, scores };
}
